import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconLayersIntersect,
  IconLogin2,
  IconMenu2,
  IconPlus,
  IconSearch,
  IconTable,
  IconLayoutKanban,
} from '@tabler/icons-react';
import { User } from '../../../entities/user/model/types';
import { getManagerColor, getManagerInitials } from '../../../shared/lib/utils';
import { pb } from '../../../shared/api/pocketbase';
import { TaskControl } from '../../../features/task-control';

const PER_PAGE = 5;

interface SuggestionItem {
  id: string;
  title: string;
  num?: number | null;
}

interface TopbarProps {
  user: User;
  onLogout: () => void;
  onSidebarToggle?: () => void;
  // kanban-only props (optional)
  searchQuery?: string;
  viewMode?: 'kanban' | 'table';
  onCreateLead?: () => void;
  onSearchChange?: (value: string) => void;
  onViewModeChange?: (mode: 'kanban' | 'table') => void;
  onSearchSelect?: (id: string) => void;
  filterDropdown?: ReactNode;
}

const Topbar: React.FC<TopbarProps> = ({
  user,
  onLogout,
  onSidebarToggle,
  searchQuery,
  viewMode,
  onCreateLead,
  onSearchChange,
  onViewModeChange,
  onSearchSelect,
  filterDropdown,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isSugLoading, setIsSugLoading] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef(1);
  const termRef = useRef('');

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDropdownOpen]);

  const buildFilter = (term: string): string | undefined => {
    if (!term.trim()) return undefined;
    const safe = term.replace(/"/g, '');
    const safeLower = safe.toLowerCase();
    const safeTitle = safeLower.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    const variants = [...new Set([safe, safeLower, safeTitle])];
    const titlePart = variants.map(v => `title ~ "${v}"`).join(' || ');
    const digits = safe.replace(/\D/g, '');
    let phonePart: string;
    if (digits.length >= 10) {
      const core = digits.length === 11 ? digits.slice(1) : digits.slice(0, 10);
      const formatted = `+7 (${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6, 8)}-${core.slice(8, 10)}`;
      phonePart = `phone = "${formatted}"`;
    } else {
      phonePart = `phone ~ "${digits.length >= 3 ? digits : safe}"`;
    }
    return `${titlePart} || id ~ "${safe}" || ${phonePart}`;
  };

  const loadPage = useCallback(async (term: string, page: number, replace: boolean) => {
    setIsSugLoading(true);
    try {
      const result = await pb.collection('tasks').getList<{ id: string; title: string; num: number }>(
        page,
        PER_PAGE,
        { sort: '-created', fields: 'id,title,num', filter: buildFilter(term) },
      );
      const items = result.items.map(l => ({ id: l.id, title: l.title ?? '', num: l.num }));
      setSuggestions(prev => replace ? items : [...prev, ...items]);
      setHasMore(result.page < result.totalPages);
    } catch {
      // ignore
    } finally {
      setIsSugLoading(false);
    }
  }, []);

  // Load fresh suggestions when query changes
  useEffect(() => {
    if (!onSearchChange) return; // only in kanban context
    termRef.current = searchQuery ?? '';
    pageRef.current = 1;
    const timer = window.setTimeout(() => {
      loadPage(termRef.current, 1, true);
    }, searchQuery ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [searchQuery, loadPage, onSearchChange]);

  const handleFocus = () => {
    setIsDropdownOpen(true);
    if (suggestions.length === 0) {
      loadPage(termRef.current, 1, true);
    }
  };

  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!hasMore || isSugLoading) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      const nextPage = pageRef.current + 1;
      pageRef.current = nextPage;
      loadPage(termRef.current, nextPage, false);
    }
  };

  const showDropdown = isDropdownOpen && onSearchChange !== undefined && suggestions.length > 0;

  const initials = getManagerInitials(user.name || user.email, user.email);
  const avatarColor = getManagerColor(user.id || user.name || user.email);
  const roleLabel = user.role === 'ADMIN'
    ? 'Администратор'
    : user.role === 'MANAGER'
      ? 'Тимлид'
      : 'Менеджер';

  return (
    <div className="topbar">
      <div className="topbar-logo" onClick={() => navigate('/kanban')} style={{ cursor: 'pointer' }}>
        <div className="ti">
          <IconLayersIntersect size={17} stroke={2.2} />
        </div>
        CRM<span>Panel</span>
      </div>

      {onSidebarToggle && (
        <button className="topbar-sidebar-toggle" onClick={onSidebarToggle} type="button" title="Меню">
          <IconMenu2 size={18} stroke={2} />
        </button>
      )}

      <div className="tsep" />

      {searchQuery !== undefined && onSearchChange && (
        <div className="topbar-search-wrap" ref={searchWrapRef} style={{ position: 'relative' }}>
          <IconSearch size={15} stroke={2} />
          <input
            className="topbar-search"
            type="text"
            name="lead-search"
            value={searchQuery}
            onChange={event => onSearchChange(event.target.value)}
            onFocus={handleFocus}
            placeholder="Поиск по ФИО, телефону или ID"
          />
          {showDropdown && (
            <div
              className="lead-search-dropdown"
              style={{ maxHeight: 220, overflowY: 'auto' }}
              onScroll={handleDropdownScroll}
            >
              {suggestions.map(lead => (
                <div
                  key={lead.id}
                  className="lead-search-option"
                  onMouseDown={() => {
                    onSearchSelect?.(lead.id);
                    setIsDropdownOpen(false);
                  }}
                >
                  <span className="lead-search-opt-title">
                    {lead.num ? `#${lead.num} — ` : ''}{lead.title || `#${lead.id.slice(-4)}`}
                  </span>
                </div>
              ))}
              {isSugLoading && (
                <div className="lead-search-option" style={{ justifyContent: 'center', opacity: 0.5 }}>
                  <div className="lead-search-spinner" />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="tsp" />

      {viewMode !== undefined && onViewModeChange && (
        <div className="view-toggle">
          <button
            className={`view-toggle-btn${viewMode === 'kanban' ? ' active' : ''}`}
            onClick={() => onViewModeChange('kanban')}
            type="button"
            title="Канбан"
          >
            <IconLayoutKanban size={16} stroke={2} />
          </button>
          <button
            className={`view-toggle-btn${viewMode === 'table' ? ' active' : ''}`}
            onClick={() => onViewModeChange('table')}
            type="button"
            title="Таблица"
          >
            <IconTable size={16} stroke={2} />
          </button>
        </div>
      )}

      {onCreateLead && (
        <button className="btn-create-lead" onClick={onCreateLead} type="button">
          <IconPlus size={15} stroke={2.2} />
          Создать лида
        </button>
      )}

      {filterDropdown}

      <TaskControl userId={user.id} role={user.role} />

      <div className="tsep" />
      <div
        className={`user-badge${pathname === '/profile' ? ' active' : ''}`}
        onClick={() => navigate('/profile')}
        style={{ cursor: 'pointer' }}
      >
        <div className="user-av" style={{ background: avatarColor }}>{initials}</div>
        <div>
          <div className="user-name">{user.name || user.email}</div>
          <div className="user-role">{roleLabel}</div>
        </div>
      </div>
      <button className="btn-out" onClick={onLogout} type="button">
        <IconLogin2 size={13} stroke={2.5} />
        Выйти
      </button>
    </div>
  );
};

export { Topbar };
